import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LetterViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLetter();
  }, [id]);

  const fetchLetter = async () => {
    try {
      const { data } = await axios.get(`${API}/letters/${id}`, { withCredentials: true });
      setLetter(data);
      setContent(data.content);
    } catch (error) {
      toast.error('Letter not found');
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/letters/${id}`, { content }, { withCredentials: true });
      toast.success('Letter saved');
      setEditing(false);
      fetchLetter();
    } catch (error) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleMail = async () => {
    if (!window.confirm('Send this letter for printing and mailing? This action cannot be undone.')) return;
    try {
      await axios.post(`${API}/letters/${id}/mail`, {}, { withCredentials: true });
      toast.success('Letter queued for mailing! Delivery timing depends on the facility.');
      fetchLetter();
    } catch (error) {
      toast.error('Failed to send for mailing');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this letter permanently?')) return;
    try {
      await axios.delete(`${API}/letters/${id}`, { withCredentials: true });
      toast.success('Letter deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!letter) return null;

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="mb-4" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>
                {letter.occasion || 'Personal Letter'}
              </h1>
              <p className="text-sm text-[#737373]">
                Created {new Date(letter.created_at).toLocaleDateString()} · Status: <span className={letter.status === 'mailed' ? 'text-green-700' : ''}>{letter.status}</span>
              </p>
            </div>
            <div className="flex gap-2">
              {letter.status === 'draft' && (
                <>
                  {editing ? (
                    <Button onClick={handleSave} disabled={saving} className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="save-btn">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
                    </Button>
                  ) : (
                    <Button onClick={() => setEditing(true)} variant="outline" className="border-[#E5E0D8]" data-testid="edit-btn">
                      Edit
                    </Button>
                  )}
                  <Button onClick={handleMail} variant="outline" className="border-[#E5E0D8]" data-testid="mail-btn">
                    <Send className="w-4 h-4 mr-2" />Mail
                  </Button>
                  <Button onClick={handleDelete} variant="outline" className="border-red-300 text-red-700" data-testid="delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-12 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          {editing ? (
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[500px] handwriting text-lg border-none focus-visible:ring-0 p-0 resize-none"
              data-testid="letter-content-editor"
            />
          ) : (
            <div className="handwriting text-xl leading-relaxed whitespace-pre-wrap" data-testid="letter-content">
              {content}
            </div>
          )}
        </div>

        {letter.status === 'mailed' && (
          <div className="mt-6 bg-[#F5EBE0] border border-[#B94A3E]/20 rounded-md p-6 text-center">
            <p className="text-sm text-[#737373]">This letter has been sent for printing and mailing. Delivery timing depends on the facility's mail-processing rules.</p>
          </div>
        )}
      </div>
    </div>
  );
}
